import { sumCents } from '../../shared/money/cents.js'
import type { Cents } from '../../shared/money/cents.js'
import type { CivilDate } from '../../shared/date/civil.js'
import type { Category } from './category.js'
import type { Transaction } from './transaction.js'

/**
 * O campo que impede contar o mesmo dinheiro duas vezes, e as duas derivações
 * que o acompanham. Transcrição executável de
 * `Docs/03-Arquitetura/Competência e caixa.md`, a nota mais importante da
 * arquitetura.
 *
 * As duas perguntas do topo do [[Dashboard]] viram somas sobre recortes
 * próprios. Elas **não** são filtros disjuntos, e a tabela mostra por quê:
 *
 * | `cashImpact` | Exemplos | Competência (`transactionDate`) | Caixa (`settlementDate`) |
 * |---|---|---|---|
 * | `direct` | Pix, débito, dinheiro, boleto | sim | sim |
 * | `deferred` | compra no cartão | sim | não |
 * | `settlement` | pagamento de fatura | não | sim |
 *
 * **`direct` é ✅ nas duas colunas.** Um Pix está na competência e no caixa do
 * mesmo mês, e isso não conta nada duas vezes: é um movimento só, e as duas
 * perguntas são diferentes com a mesma resposta.
 *
 * O que o modelo promete é mais estreito: **`deferred` e `settlement` nunca
 * caem na mesma visão**, então a compra no cartão e o pagamento da fatura dela
 * não se encontram em lugar nenhum. É daí que sai "nenhum real é contado duas
 * vezes".
 *
 * Esta transcrição já afirmou "nenhuma transação aparece nas duas listas", e não
 * é verdade — decisão 96(b). A outra metade da afirmação errada estava
 * **quebrada em duas linhas** aqui, com a palavra "disjuntos" sozinha na
 * segunda: foi assim que ela escapou de uma varredura por linha. Refutada por
 * `accrual-and-cash.spec.ts` e travada por `src/test/vault-consistency.test.ts`.
 */

/**
 * **Derivado, não redeclarado** — decisão 89. Repetir os três literais aqui
 * compilaria limpo mesmo já divergente: duas uniões idênticas são mutuamente
 * atribuíveis, o barrel não tem o que acusar porque `Transaction['cashImpact']`
 * é um tipo inline e anônimo, e `tsc -b --noEmit` sai em 0 com uma `CashImpact`
 * de quatro membros reexportada. O alias torna a divergência impossível em vez
 * de detectável.
 *
 * As travas de identidade — a que casa com `Transaction` e a que casa com os
 * três membros da tabela da nota — estão em `cash-impact.test.ts`.
 */
export type CashImpact = Transaction['cashImpact']

/**
 * Como `cashImpact` é derivado, na ordem da nota:
 *
 * ```
 * instrumento é cartão                        → deferred
 * instrumento é conta e concilia com Invoice  → settlement
 * caso contrário                              → direct
 * ```
 *
 * **O instrumento vem primeiro, e a ordem não é estilo.** Compra de cartão é
 * `deferred` mesmo que alguma conciliação tenha apontado para ela por engano:
 * deixar `settlesInvoiceId` decidir aqui tiraria a compra da competência — e
 * competência é a visão que o app mostra por padrão, então o erro apareceria no
 * número mais visível do produto. Há teste só para isso.
 *
 * `settlesInvoiceId` é a fatura que esta transação quita, se alguma. A
 * conciliação em si é da Fase 3; aqui ela chega pronta, como parâmetro. As três
 * formas de "nenhuma" — campo ausente, `null` e `''` — caem todas em `direct`,
 * porque a checagem é de verdade e não de presença.
 */
export function deriveCashImpact(
  source: Transaction['source'],
  context: {
    settlesInvoiceId?: string | null
    /**
     * `true` quando esta linha é o **espelho** de um movimento já contado do
     * outro lado — hoje, o `'Pagamento recebido'` do cartão quando a conta paga
     * a fatura. Quem decide é a conciliação, que enxerga os dois lados.
     */
    isInternalMirror?: boolean
  },
): CashImpact {
  // **O espelho vem ANTES do instrumento, e a ordem é o ponto.** Um
  // `'Pagamento recebido'` está num cartão, então cairia em `deferred` — que é
  // competência — se o instrumento decidisse primeiro. É exatamente o que se
  // quer evitar: ele abateria da competência um dinheiro que ninguém deixou de
  // gastar. Medido: 48 dessas, somando +183.508,97 (decisão 138).
  if (context.isInternalMirror === true) return 'internal'
  if (source.instrument.type === 'creditCard') return 'deferred'
  return context.settlesInvoiceId ? 'settlement' : 'direct'
}

/**
 * Quando o dinheiro saiu da conta:
 *
 * - `direct` → igual ao `transactionDate`
 * - `deferred` → `null`. **Sempre null**, não até a fatura ser paga (decisão
 *   94). Quem carrega o dinheiro da compra é a `Invoice`, e quem o tira da
 *   conta é a transação de pagamento, que é `settlement`. Escrever o
 *   vencimento da fatura aqui é a contagem dupla que a ADR-0004 existe para
 *   impedir: o caixa somaria a compra **e** o pagamento, e a Pluggy entrega os
 *   dois.
 * - `settlement` → o `transactionDate` do próprio pagamento
 *
 * `transactionDate` e não `postedAt`: a data da compra é a que a nota nomeia
 * nos três casos. O lançamento pela instituição serve para exibição e
 * conciliação.
 *
 * **A regra do instrumento não é repetida aqui, e isto é escolha de desenho, não
 * garantia.** Ela mora em `deriveCashImpact` e em nenhum outro lugar; esta
 * função é uma função de `impact`, e o chamador encadeia as duas. Duplicá-la
 * daria dois lugares para errá-la e um a mais para elas discordarem entre si.
 * Dito isso: **nenhum teste prende esta frase**, e nem poderia — um `if
 * (source.instrument.type === 'creditCard') return null` acrescentado aqui é
 * equivalente em comportamento para todo `impact` que `deriveCashImpact`
 * produz, então ele sobrevive a qualquer varredura de mutação (verificado). O
 * que os testes prendem é o encadeamento das duas funções, que é como o
 * chamador as usa.
 */
export function deriveSettlementDate(
  source: Transaction['source'],
  impact: CashImpact,
): CivilDate | null {
  // `internal` junto com `deferred`: os dois ficam fora do caixa, e é a
  // ausência de `settlementDate` que os tira — não um segundo filtro. Para o
  // espelho isso é o que fecha a conta: o dinheiro sai UMA vez, pela conta.
  return impact === 'deferred' || impact === 'internal' ? null : source.transactionDate
}

/**
 * Quais impactos entram na **competência**, ao lado do tipo que os define.
 *
 * `Record<CashImpact, boolean>` e não um array: acrescentar um membro à união
 * **obriga** uma entrada aqui — foi o que aconteceu quando `internal` entrou
 * (decisão 138), e o `tsc` cobrou.
 *
 * Morava em `monthly-view.ts`, e mudou de casa em 2026-08-31 porque
 * `totalSpend` fazia a mesma pergunta com outras palavras (`!== 'settlement'`)
 * — e as duas **discordavam** sobre `internal`, que o mapa excluía e o filtro
 * não. Um lugar só, junto do tipo.
 */
export const IN_COMPETENCE: Record<CashImpact, boolean> = {
  direct: true,
  deferred: true,
  settlement: false,
  // O espelho de um movimento já contado do outro lado. Nem competência nem
  // caixa — quem o tira do caixa é o `settlementDate` nulo. Decisão 138.
  internal: false,
}

/**
 * Os `cashImpact` que entram na competência, derivados do mapa acima — é este
 * array que vira o `where('cashImpact', 'in', …)` do adapter. Derivar em vez de
 * repetir os literais é o que impede a consulta e o predicado de discordarem.
 */
export const COMPETENCE_IMPACTS: CashImpact[] = (
  Object.keys(IN_COMPETENCE) as CashImpact[]
).filter((impact) => IN_COMPETENCE[impact])

/**
 * O mínimo que uma soma de gasto precisa saber de uma transação. Nomeado, e
 * não inline, porque quem monta a lista é a camada de consulta (Fase 2, Task 8)
 * e ela precisa poder nomear o alvo do `map`.
 *
 * **`categoryKind` é anulável, e `null` conta como gasto.**
 * `Transaction.enrichment.categoryId` é `string | null`, então transação sem
 * categoria existe e é comum — é o estado de toda transação recém-sincronizada,
 * antes de alguém a categorizar. Exigir um `kind` aqui obrigaria quem monta a
 * lista a **inventar** um, e o default errado (`'transfer'`) somiria com o gasto
 * de todo relatório em silêncio. Com `null` no tipo, a escolha deixa de existir:
 * não categorizado é gasto até que se diga o contrário, que é o comportamento
 * conservador — o número aparece grande demais em vez de pequeno demais.
 */
export type SpendEntry = {
  cashImpact: CashImpact
  amountCents: Cents
  /** `null` quando a transação ainda não foi categorizada. Conta como gasto. */
  categoryKind: Category['kind'] | null
  /**
   * Quanto as entradas abatem deste gasto, positivo. **Só a competência o
   * aplica** — o caixa mostra o dinheiro que se moveu, e ele se moveu. Ausente
   * é zero: só `spendEntriesOf` o preenche, e os literais de teste anteriores
   * ao abatimento continuam valendo.
   */
  offsetCents?: Cents
  /** Entrada ligada a uma compra: fora da competência; no caixa, entrada comum. */
  absorbed?: boolean
}

/**
 * Soma de gasto. **Duas exclusões, com causas diferentes, e por isso dois
 * filtros:**
 *
 * - `settlement` sai porque **pagar fatura não é consumir**. O consumo já foi
 *   contado quando cada compra entrou; se o pagamento entrasse, agosto teria as
 *   compras de agosto mais as de julho de novo.
 * - categoria `transfer` sai porque **mover dinheiro entre contas próprias não
 *   é consumo**. Transferir da conta A para a poupança B gera duas transações,
 *   as duas `direct` — o dinheiro de fato se moveu —, e nenhuma é gasto.
 *
 * Nenhuma das duas cobre a outra: um pagamento de fatura é `settlement` com
 * categoria de despesa, e uma transferência é `direct` com categoria
 * `transfer`. Juntá-las num filtro só deixaria uma das duas classes passar.
 *
 * **A soma é assinada.** O sinal mora em `Transaction.source.amountCents` e só
 * ali (`@shared/money/cents`), então um estorno de R$ 30 sobre uma compra de
 * R$ 100 deixa R$ 70 de gasto, e não R$ 130. Tirar o sinal com `absCents` faria
 * estorno virar gasto.
 *
 * Categoria `income` **não** é excluída: as exclusões são as duas de cima, e
 * quem decide se um relatório mistura receita e despesa é quem monta a lista.
 * Categoria **ausente** (`null`) também não: transação não categorizada conta
 * como gasto, pelo motivo escrito em `SpendEntry`. O `!== 'transfer'` já deixa
 * `null` passar — o que não pode aparecer aqui é uma guarda de presença
 * (`categoryKind !== null &&`), que sumiria com ela.
 */
export function totalSpend(entries: SpendEntry[]): Cents {
  return sumCents(naCompetencia(entries).map((entry) => entry.amountCents))
}

/**
 * As entradas que contam na competência: **pergunta ao mapa**, em vez de
 * nomear um `cashImpact`.
 *
 * A versão anterior escrevia `!== 'settlement'`, o que é a mesma pergunta com
 * outras palavras — e ela ficou errada sozinha quando `internal` entrou na
 * união, porque nomear um valor não acompanha a chegada de outro.
 */
function naCompetencia(entries: SpendEntry[]): SpendEntry[] {
  return semTransferencia(
    entries
      .filter((entry) => IN_COMPETENCE[entry.cashImpact] && entry.absorbed !== true)
      // O gasto abatido vale o líquido, na competência da COMPRA — o abatimento
      // mora na linha do gasto. Ver `abatimento.ts`.
      .map((entry) => ({ ...entry, amountCents: entry.amountCents + (entry.offsetCents ?? 0) })),
  )
}

/**
 * **Só o que SAIU**, na competência — o número que responde "quanto gastei?".
 *
 * Existe porque `totalSpend` é um **líquido**: ele soma entrada e saída juntas,
 * e não exclui receita. Medido no extrato real: em julho ele dá **+19.190,80**,
 * enquanto as saídas somam **−42.241,71** e as entradas **+61.432,51**. Chamar
 * aquele primeiro número de "Gastos" — como a tela fazia — é dizer que a casa
 * gastou dezenove mil positivos.
 *
 * Decisão 141: o número grande da tela passa a ser este, com as entradas e o
 * líquido ao lado.
 *
 * O sinal é preservado — devolve negativo, como as transações que soma. Quem
 * quiser mostrar sem sinal usa `absCents` na fronteira da tela, que é onde
 * formatação mora.
 */
export function totalOutflow(entries: SpendEntry[]): Cents {
  return sumCents(saidasOuEntradas(naCompetencia(entries), 'saida'))
}

/** **Só o que ENTROU**, na competência. Irmã de `totalOutflow`. */
export function totalInflow(entries: SpendEntry[]): Cents {
  return sumCents(saidasOuEntradas(naCompetencia(entries), 'entrada'))
}

/**
 * As mesmas duas, para a visão de **caixa** — e elas existem separadas porque
 * **o filtro é outro**.
 *
 * `totalCash` não filtra `cashImpact` nenhum, e é de propósito: `settlement`
 * **é** caixa (o pagamento da fatura tirou dinheiro da conta), e quem deixa a
 * compra de cartão de fora é o `settlementDate` nulo, não um filtro. Reusar
 * `naCompetencia` aqui excluiria justamente o pagamento da fatura da visão que
 * existe para mostrá-lo.
 *
 * A única exclusão que as duas visões compartilham é `transfer`.
 */
export function totalCashOutflow(entries: SpendEntry[]): Cents {
  return sumCents(saidasOuEntradas(semTransferencia(entries), 'saida'))
}

export function totalCashInflow(entries: SpendEntry[]): Cents {
  return sumCents(saidasOuEntradas(semTransferencia(entries), 'entrada'))
}

function semTransferencia(entries: SpendEntry[]): SpendEntry[] {
  return entries.filter((entry) => entry.categoryKind !== 'transfer')
}

/**
 * A separação por sinal, num lugar só. **Zero fica de fora das duas** — não é
 * saída nem entrada, e somá-lo aos dois lados não mudaria nada mas faria a
 * contagem de itens mentir se alguém a usasse.
 */
function saidasOuEntradas(entries: SpendEntry[], lado: 'saida' | 'entrada'): Cents[] {
  return entries
    .filter((entry) => (lado === 'saida' ? entry.amountCents < 0 : entry.amountCents > 0))
    .map((entry) => entry.amountCents)
}

/**
 * A soma da **visão de caixa**: o que de fato entrou e saiu das contas no mês.
 * Irmã de `totalSpend`, e a diferença entre as duas é uma linha de filtro que
 * responde por uma linha da definição de pronto da fase.
 *
 * **`settlement` fica**, e é aqui que a assimetria com `totalSpend` importa.
 * `Docs/05-Plano/Fase 2 - Núcleo financeiro.md` pede que *"a soma de caixa de
 * agosto conte o pagamento uma vez, não duas"* — uma vez, não zero. Tirar o
 * pagamento de fatura desta soma esconderia a maior saída do mês da conta
 * corrente, e a pergunta que esta visão responde é literalmente "quanto saiu?".
 *
 * Não há contagem dupla nisso, e o motivo é o desenho inteiro do modelo: na
 * visão de caixa a compra no cartão **já não está**, porque `deferred` tem
 * `settlementDate` nulo para sempre (decisão 94). O filtro que `totalSpend`
 * aplica ao `cashImpact` seria, aqui, uma segunda subtração do mesmo dinheiro —
 * pela outra ponta.
 *
 * **`transfer` sai**, pela mesma razão de `totalSpend` e pela regra que
 * `Docs/04-Design/Telas/Transações.md` escreve para esta tela: transferência
 * entre contas próprias aparece na lista, marcada, e fica **fora dos dois
 * totais**. Mover R$ 1.000 da corrente para a poupança gera duas transações
 * `direct`, as duas na visão de caixa, e nenhuma delas é dinheiro que a casa
 * ganhou ou perdeu.
 *
 * **A soma é assinada**, como a de `totalSpend`: o sinal mora em
 * `Transaction.source.amountCents` e só ali. Um salário e um aluguel no mesmo
 * mês se compensam, que é o que "quanto sobrou de movimento" quer dizer.
 *
 * Aplicada a uma lista de **competência** ela devolve o mesmo que `totalSpend`,
 * porque essa lista nunca tem `settlement`. Isso não as torna a mesma função: o
 * que as separa é a lista de caixa, e é lá que a diferença é o número inteiro.
 */
export function totalCash(entries: SpendEntry[]): Cents {
  return sumCents(semTransferencia(entries).map((entry) => entry.amountCents))
}
